import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sparkles, Download, FileImage, FileText, Share2, Plus, Megaphone, CalendarDays, DollarSign, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import { RegistrationTab, PaymentsTab, BudgetTab, SubmissionsTab, KitMagazineTab, ReportsTab, ChecklistTab } from "./conference-kit/ConferenceKitTabs";
import { FlyerBuilderPro } from "./conference-kit/FlyerBuilderPro";
import { PublishWorkspace } from "./conference-kit/PublishWorkspace";
import { YearArchiveBar } from "./conference-kit/YearArchiveBar";
import { ExportMenu } from "./conference-kit/ExportMenu";
import { ConsolidatedReportButton } from "./conference-kit/ConsolidatedReportButton";
import { LatestConferenceEditor } from "./conference-kit/LatestConferenceEditor";
import { RegionalFlyerDownload } from "./conference-kit/RegionalFlyerDownload";
import { AIFlyerEditor } from "./conference-kit/AIFlyerEditor";
import CancellationRequestsManager from "./CancellationRequestsManager";

interface KitEvent {
  id: string;
  region: string;
  region_id?: string | null;
  flyer_url?: string | null;
  conference_year?: number | null;
  title: string;
  theme?: string | null;
  scripture?: string | null;
  venue_name?: string | null;
  venue_address?: string | null;
  city?: string | null;
  state?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  speaker_name?: string | null;
  speaker_title?: string | null;
  worship_team?: string | null;
  coordinator_name?: string | null;
  coordinator_email?: string | null;
  coordinator_phone?: string | null;
  registration_url?: string | null;
  flyer_approved?: boolean;
  status?: string;
}

const TEAL = "#006B6B";
const GOLD = "#C9A84C";

const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const ZellePolicy = () => (
  <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-xs">
    ⚠️ <strong>All registration fees must be paid via Zelle to the OFUESI Organization Account.</strong> Payments to individuals are NOT authorized and will not be credited.
  </div>
);

const ScriptureFooter = () => (
  <p className="text-center text-xs italic text-muted-foreground mt-6">
    "Let all things be done decently and in order." — 1 Corinthians 14:40
  </p>
);

interface Props {
  userRegionId?: string | null;
  userRole: string;
  isGlobalAdmin: boolean;
}

export const ConferenceKitDashboard: React.FC<Props> = ({ userRegionId, userRole, isGlobalAdmin }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [userRegion, setUserRegion] = useState<string>("");
  useEffect(() => {
    if (!userRegionId) { setUserRegion(""); return; }
    (async () => {
      const { data } = await supabase.from("regions").select("name").eq("id", userRegionId).maybeSingle();
      setUserRegion((data as any)?.name || "");
    })();
  }, [userRegionId]);
  const [events, setEvents] = useState<KitEvent[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() => searchParams.get("kitTab") || "dashboard");
  const [workspace, setWorkspace] = useState<"planning" | "publish">(() =>
    searchParams.get("kitWorkspace") === "publish" ? "publish" : "planning",
  );
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  const yearOf = (e: KitEvent) =>
    (e as any).conference_year ?? (e.start_date ? new Date(e.start_date).getFullYear() : new Date().getFullYear());

  const yearFilteredEvents = useMemo(
    () => (selectedYear === null ? events : events.filter((e) => yearOf(e) === selectedYear)),
    [events, selectedYear],
  );

  const selected = yearFilteredEvents.find((e) => e.id === selectedId) || yearFilteredEvents[0] || null;
  const isArchived = selected?.status === "archived";

  const loadEvents = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("conference_kit_events" as any)
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast({ title: "Failed to load conferences", description: error.message, variant: "destructive" });
    } else {
      const list = (data as unknown as KitEvent[]) || [];
      setEvents(list);
      if (list.length && !selectedId) setSelectedId(list[0].id);
    }
    setLoading(false);
  };

  useEffect(() => { loadEvents(); }, []);

  const updateKitRouteState = (patch: { workspace?: "planning" | "publish"; tab?: string }) => {
    const next = new URLSearchParams(searchParams);
    if (patch.workspace) next.set("kitWorkspace", patch.workspace);
    if (patch.tab) next.set("kitTab", patch.tab);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: TEAL }}>🎪 Conference Kit</h2>
          <p className="text-sm text-muted-foreground">
            {isGlobalAdmin ? "All Regions" : userRegion || "Your Region"} · Plan, promote, register, and report your regional conference
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <YearArchiveBar
            events={events as any}
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
            region={isGlobalAdmin ? undefined : userRegion}
            isGlobalAdmin={isGlobalAdmin}
            onArchived={loadEvents}
          />
          {yearFilteredEvents.length > 0 && (
            <Select value={selected?.id || ""} onValueChange={setSelectedId}>
              <SelectTrigger className="w-[260px]"><SelectValue placeholder="Select conference" /></SelectTrigger>
              <SelectContent>
                {yearFilteredEvents.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.title} {e.region ? `· ${e.region}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button onClick={() => setWizardOpen(true)} style={{ backgroundColor: GOLD, color: "white" }}>
            <Plus className="w-4 h-4 mr-1" /> New Conference
          </Button>
          <ConsolidatedReportButton
            isGlobalAdmin={isGlobalAdmin}
            userRegion={userRegion || undefined}
            defaultYear={selectedYear ?? (selected ? ((selected as any).conference_year || (selected.start_date ? new Date(selected.start_date).getFullYear() : new Date().getFullYear())) : new Date().getFullYear())}
          />
          {selected && (
            <ExportMenu
              filenameBase={`conference-kit-${selected.region}-${(selected as any).conference_year || new Date().getFullYear()}`}
              label="Export All"
              sections={[
                {
                  title: "Conference",
                  headers: ["Field", "Value"],
                  rows: [
                    ["Title", selected.title],
                    ["Region", selected.region],
                    ["Theme", selected.theme || ""],
                    ["Scripture", selected.scripture || ""],
                    ["Venue", selected.venue_name || ""],
                    ["City", selected.city || ""],
                    ["Start", selected.start_date || ""],
                    ["End", selected.end_date || ""],
                    ["Status", selected.status || ""],
                  ],
                },
              ]}
            />
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" /></div>
      ) : yearFilteredEvents.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center space-y-3">
            <Megaphone className="mx-auto h-10 w-10 text-muted-foreground" />
            <h3 className="text-lg font-semibold">No conferences for {selectedYear}</h3>
            <p className="text-sm text-muted-foreground">Create a conference to start planning.</p>
            <Button onClick={() => setWizardOpen(true)} style={{ backgroundColor: TEAL, color: "white" }}>
              <Plus className="w-4 h-4 mr-1" /> Create Conference
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Top-level workspace switch: Planning vs Publish */}
          <Tabs value={workspace} onValueChange={(v) => { setWorkspace(v as any); updateKitRouteState({ workspace: v as any }); }}>
            <TabsList>
              <TabsTrigger value="planning">🗂️ Planning</TabsTrigger>
              <TabsTrigger value="publish" disabled={isArchived}>📤 Publish</TabsTrigger>
            </TabsList>

            <TabsContent value="planning" className="mt-3">
              {isArchived && (
                <div className="rounded-md border border-muted bg-muted/40 px-3 py-2 text-xs mb-2">
                  📚 Archived year — read-only history. Use the year selector to switch.
                </div>
              )}
              <Tabs value={tab} onValueChange={(v) => { setTab(v); updateKitRouteState({ workspace: "planning", tab: v }); }}>
                <TabsList className="flex flex-wrap h-auto">
                  <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
                  <TabsTrigger value="latest">Latest Conference</TabsTrigger>
                  <TabsTrigger value="flyer">Flyer Builder</TabsTrigger>
                  <TabsTrigger value="registration">Registration</TabsTrigger>
                  <TabsTrigger value="payments">Fee & Payments</TabsTrigger>
                  <TabsTrigger value="budget">Budget</TabsTrigger>
                  <TabsTrigger value="submissions">Submissions</TabsTrigger>
                  <TabsTrigger value="kit">KIT Magazine</TabsTrigger>
                  <TabsTrigger value="reports">Reports</TabsTrigger>
                  <TabsTrigger value="checklist">Checklist</TabsTrigger>
                  <TabsTrigger value="cancellations">🚫 Cancellations</TabsTrigger>
                </TabsList>

                <TabsContent value="dashboard" className="mt-4">
                  {selected && <DashboardTab event={selected} />}
                </TabsContent>
                <TabsContent value="latest" className="mt-4">
                  {selected?.region_id && (
                    <LatestConferenceEditor
                      regionId={selected.region_id}
                      region={selected.region}
                      year={selected.conference_year || new Date().getFullYear()}
                      isGlobalAdmin={isGlobalAdmin}
                    />
                  )}
                  {!selected?.region_id && (
                    <div className="text-sm text-muted-foreground">This conference is not linked to a region yet.</div>
                  )}
                </TabsContent>
                <TabsContent value="flyer" className="mt-4">
                  {selected && (
                    <div className="space-y-3">
                      {!selected.flyer_approved && (
                        <div className="rounded-md border border-yellow-300 bg-yellow-50 text-yellow-900 px-3 py-2 text-xs">
                          ⚠️ Awaiting Regional Coordinator Approval before distribution.
                        </div>
                      )}
                      <RegionalFlyerDownload
                        region={selected.region}
                        regionId={selected.region_id || undefined}
                      />
                      <AIFlyerEditor
                        region={selected.region}
                        regionId={selected.region_id || undefined}
                        eventId={selected.id}
                        initialFlyerUrl={selected.flyer_url || undefined}
                        onPublished={() => loadEvents()}
                      />
                      <FlyerBuilderPro event={selected} />
                      {(isGlobalAdmin || userRole === "president") && !selected.flyer_approved && (
                        <Button variant="outline" size="sm" onClick={async () => {
                          const { error } = await (supabase as any).from("conference_kit_events")
                            .update({ flyer_approved: true, flyer_approved_at: new Date().toISOString() })
                            .eq("id", selected.id);
                          if (error) toast({ title: "Approval blocked", description: error.message, variant: "destructive" });
                          else { toast({ title: "Flyer approved" }); loadEvents(); }
                        }}><CheckCircle2 className="w-4 h-4 mr-1" /> Mark Approved</Button>
                      )}
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="registration" className="mt-4">{selected && <RegistrationTab event={selected} />}</TabsContent>
                <TabsContent value="payments" className="mt-4">{selected && <PaymentsTab event={selected} />}</TabsContent>
                <TabsContent value="budget" className="mt-4">{selected && <BudgetTab event={selected} canApprove={isGlobalAdmin || userRole === "president"} />}</TabsContent>
                <TabsContent value="submissions" className="mt-4">{selected && <SubmissionsTab event={selected} />}</TabsContent>
                <TabsContent value="kit" className="mt-4">{selected && <KitMagazineTab event={selected} />}</TabsContent>
                <TabsContent value="reports" className="mt-4">{selected && <ReportsTab event={selected} isGlobalAdmin={isGlobalAdmin} />}</TabsContent>
                <TabsContent value="checklist" className="mt-4">{selected && <ChecklistTab event={selected} />}</TabsContent>
                <TabsContent value="cancellations" className="mt-4">
                  {selected && (
                    <div className="space-y-3">
                      <div className="rounded-md border border-sky-200 bg-sky-50 text-sky-900 px-3 py-2 text-xs">
                        Approving a cancellation removes the member or family from registrations, so live counts and exports update automatically.
                      </div>
                      <CancellationRequestsManager region={selected.region} />
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </TabsContent>

            <TabsContent value="publish" className="mt-3">
              {selected && (
                <PublishWorkspace event={selected as any} userRole={userRole} isGlobalAdmin={isGlobalAdmin} />
              )}
            </TabsContent>
          </Tabs>
        </>
      )}

      <ScriptureFooter />

      <NewConferenceWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        defaultRegion={userRegion || ""}
        onCreated={(id) => { loadEvents().then(() => setSelectedId(id)); }}
      />
    </div>
  );
};

const ComingSoonCard: React.FC<{ label: string }> = ({ label }) => (
  <Card>
    <CardContent className="py-12 text-center">
      <Badge variant="secondary" className="mb-3">Phase 2+</Badge>
      <p className="text-sm text-muted-foreground capitalize">
        The <strong>{label}</strong> module is being rolled out in the next phase. Phase 1 ships Dashboard, Flyer Builder, and Conference setup.
      </p>
    </CardContent>
  </Card>
);

// ---------------- Dashboard Tab ----------------
const DashboardTab: React.FC<{ event: KitEvent }> = ({ event }) => {
  const [stats, setStats] = useState({ regs: 0, paid: 0, fees: 0 });
  const [checklistDone, setChecklistDone] = useState(0);
  const [submissionsDone, setSubmissionsDone] = useState(0);

  useEffect(() => {
    (async () => {
      // Live registrations from existing conference_registrations by region
      const { count: regsCount } = await supabase
        .from("conference_registrations")
        .select("id", { count: "exact", head: true })
        .eq("region", event.region);
      const { data: paidRows } = await supabase
        .from("conference_registrations")
        .select("id, total_members")
        .eq("region", event.region)
        .eq("payment_status", "confirmed");
      setStats({
        regs: regsCount || 0,
        paid: paidRows?.length || 0,
        fees: 0, // placeholder; computed in Payments tab
      });

      const { count: cl } = await (supabase as any)
        .from("conference_checklist_items").select("id", { count: "exact", head: true })
        .eq("kit_event_id", event.id).eq("is_complete", true);
      setChecklistDone(cl || 0);

      const { count: sb } = await (supabase as any)
        .from("conference_kit_submissions").select("id", { count: "exact", head: true })
        .eq("kit_event_id", event.id).neq("status", "pending");
      setSubmissionsDone(sb || 0);
    })();
  }, [event.id, event.region]);

  const daysUntil = useMemo(() => {
    if (!event.start_date) return null;
    const ms = new Date(event.start_date).getTime() - Date.now();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  }, [event.start_date]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Total Registrations" value={stats.regs} />
        <KpiCard label="Confirmed Paid" value={stats.paid} />
        <KpiCard label="Fees Collected" value="$ —" sub="See Payments tab" />
        <KpiCard label="Days Until Conference" value={daysUntil === null ? "—" : daysUntil <= 0 ? "Complete" : daysUntil} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <ProgressCard title="Budget Status" subtitle="Estimated vs actual" value={0} max={100} />
        <ProgressCard title="Checklist Progress" subtitle={`${checklistDone} of 70 items`} value={checklistDone} max={70} />
        <ProgressCard title="Submissions Progress" subtitle={`${submissionsDone} of 9 documents`} value={submissionsDone} max={9} />
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">Active Alerts</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-2">
          {!event.flyer_approved && (
            <div className="flex items-center gap-2 text-amber-700"><AlertTriangle className="w-4 h-4" /> Flyer not yet approved by Regional Coordinator.</div>
          )}
          {daysUntil !== null && daysUntil > 0 && daysUntil <= 14 && (
            <div className="flex items-center gap-2 text-red-700"><AlertTriangle className="w-4 h-4" /> Conference is in {daysUntil} days — finalise checklist!</div>
          )}
          {checklistDone === 0 && (
            <div className="text-muted-foreground">Open the Checklist tab to begin tracking your 70+ tasks.</div>
          )}
          <ZellePolicy />
        </CardContent>
      </Card>
    </div>
  );
};

const KpiCard: React.FC<{ label: string; value: React.ReactNode; sub?: string }> = ({ label, value, sub }) => (
  <Card style={{ backgroundColor: TEAL }}>
    <CardContent className="py-4 text-white">
      <div className="text-xs uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      {sub && <div className="text-[11px] opacity-70 mt-1">{sub}</div>}
    </CardContent>
  </Card>
);

const ProgressCard: React.FC<{ title: string; subtitle: string; value: number; max: number }> = ({ title, subtitle, value, max }) => {
  const pct = max ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">{title}</div>
            <div className="text-xs text-muted-foreground">{subtitle}</div>
          </div>
          <div className="text-lg font-bold" style={{ color: TEAL }}>{pct}%</div>
        </div>
        <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: GOLD }} />
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------- Flyer Builder Tab ----------------
const FlyerBuilderTab: React.FC<{ event: KitEvent; onUpdated: () => void; canApprove: boolean }> = ({ event, onUpdated, canApprove }) => {
  const [form, setForm] = useState<KitEvent>(event);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [template, setTemplate] = useState<"classic" | "gold" | "minimal">("classic");
  const flyerRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setForm(event); }, [event.id]);

  const update = (k: keyof KitEvent, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    const { error } = await (supabase as any)
      .from("conference_kit_events")
      .update({
        title: form.title, theme: form.theme, scripture: form.scripture,
        venue_name: form.venue_name, venue_address: form.venue_address, city: form.city, state: form.state,
        start_date: form.start_date, end_date: form.end_date, start_time: form.start_time, end_time: form.end_time,
        speaker_name: form.speaker_name, speaker_title: form.speaker_title, worship_team: form.worship_team,
        coordinator_name: form.coordinator_name, coordinator_email: form.coordinator_email, coordinator_phone: form.coordinator_phone,
        registration_url: form.registration_url,
      })
      .eq("id", event.id);
    setSaving(false);
    if (error) toast({ title: "Save failed", description: error.message, variant: "destructive" });
    else { toast({ title: "Flyer details saved" }); onUpdated(); }
  };

  const generateAI = async () => {
    setAiLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-flyer-copy", {
        body: {
          title: form.title, theme: form.theme, scripture: form.scripture,
          speaker_name: form.speaker_name, speaker_title: form.speaker_title,
          venue: form.venue_name, city: form.city, state: form.state,
          start_date: form.start_date, end_date: form.end_date, region: form.region,
        },
      });
      if (error) throw error;
      const ai = data as any;
      setForm((f) => ({
        ...f,
        theme: ai?.theme_line || f.theme,
        scripture: ai?.scripture_suggestion || f.scripture,
      }));
      toast({ title: "AI generated", description: ai?.tagline ? `Tagline: ${ai.tagline}` : "Theme & scripture refined." });
    } catch (e: any) {
      toast({ title: "AI failed", description: e.message, variant: "destructive" });
    }
    setAiLoading(false);
  };

  const downloadPng = async () => {
    if (!flyerRef.current) return;
    try {
      const dataUrl = await toPng(flyerRef.current, { cacheBust: true, pixelRatio: 2 });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `flyer-${slugify(form.title || "ofuesi")}.png`;
      a.click();
    } catch (e: any) {
      toast({ title: "Download failed", description: e.message, variant: "destructive" });
    }
  };

  const downloadPdf = async () => {
    if (!flyerRef.current) return;
    const dataUrl = await toPng(flyerRef.current, { cacheBust: true, pixelRatio: 2 });
    const pdf = new jsPDF({ unit: "pt", format: "letter" });
    const w = pdf.internal.pageSize.getWidth();
    const h = pdf.internal.pageSize.getHeight();
    pdf.addImage(dataUrl, "PNG", 24, 24, w - 48, h - 48, undefined, "FAST");
    pdf.save(`flyer-${slugify(form.title || "ofuesi")}.pdf`);
  };

  const regSlug = `${slugify(form.region || "region")}-${form.start_date ? new Date(form.start_date).getFullYear() : new Date().getFullYear()}`;
  const regUrl = form.registration_url || `https://ofuesi.org/register/${regSlug}`;

  const whatsappShare = () => {
    const msg = `🎪 ${form.title}\n${form.theme || ""}\n📅 ${form.start_date || ""} ${form.end_time ? "" : ""}\n📍 ${form.venue_name || ""}, ${form.city || ""}\n🌐 Register: ${regUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const approve = async () => {
    const { error } = await (supabase as any)
      .from("conference_kit_events")
      .update({ flyer_approved: true, flyer_approved_at: new Date().toISOString() })
      .eq("id", event.id);
    if (!error) { toast({ title: "Flyer approved" }); onUpdated(); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-base">
            <span>Flyer Details</span>
            <Button size="sm" variant="outline" onClick={generateAI} disabled={aiLoading}>
              {aiLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              Generate with AI
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 max-h-[70vh] overflow-y-auto">
          <Field label="Conference Title"><Input value={form.title || ""} onChange={(e) => update("title", e.target.value)} /></Field>
          <Field label="Theme"><Input value={form.theme || ""} onChange={(e) => update("theme", e.target.value)} /></Field>
          <Field label="Scripture"><Input value={form.scripture || ""} onChange={(e) => update("scripture", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Start Date"><Input type="date" value={form.start_date || ""} onChange={(e) => update("start_date", e.target.value)} /></Field>
            <Field label="End Date"><Input type="date" value={form.end_date || ""} onChange={(e) => update("end_date", e.target.value)} /></Field>
            <Field label="Start Time"><Input value={form.start_time || ""} placeholder="9:00 AM" onChange={(e) => update("start_time", e.target.value)} /></Field>
            <Field label="End Time"><Input value={form.end_time || ""} placeholder="5:00 PM" onChange={(e) => update("end_time", e.target.value)} /></Field>
          </div>
          <Field label="Venue Name"><Input value={form.venue_name || ""} onChange={(e) => update("venue_name", e.target.value)} /></Field>
          <Field label="Venue Address"><Input value={form.venue_address || ""} onChange={(e) => update("venue_address", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="City"><Input value={form.city || ""} onChange={(e) => update("city", e.target.value)} /></Field>
            <Field label="State"><Input value={form.state || ""} onChange={(e) => update("state", e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Speaker Name"><Input value={form.speaker_name || ""} onChange={(e) => update("speaker_name", e.target.value)} /></Field>
            <Field label="Speaker Title"><Input value={form.speaker_title || ""} onChange={(e) => update("speaker_title", e.target.value)} /></Field>
          </div>
          <Field label="Worship Team"><Input value={form.worship_team || ""} onChange={(e) => update("worship_team", e.target.value)} /></Field>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Coordinator Name"><Input value={form.coordinator_name || ""} onChange={(e) => update("coordinator_name", e.target.value)} /></Field>
            <Field label="Coordinator Email"><Input value={form.coordinator_email || ""} onChange={(e) => update("coordinator_email", e.target.value)} /></Field>
            <Field label="Coordinator Phone"><Input value={form.coordinator_phone || ""} onChange={(e) => update("coordinator_phone", e.target.value)} /></Field>
          </div>
          <Field label="Registration URL (auto)"><Input value={regUrl} onChange={(e) => update("registration_url", e.target.value)} /></Field>
          <Field label="Template">
            <Select value={template} onValueChange={(v: any) => setTemplate(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="classic">Classic Teal</SelectItem>
                <SelectItem value="gold">Gold Banner</SelectItem>
                <SelectItem value="minimal">Minimal</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div className="flex gap-2 pt-2">
            <Button onClick={save} disabled={saving} style={{ backgroundColor: TEAL, color: "white" }}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Details"}
            </Button>
            {canApprove && !event.flyer_approved && (
              <Button variant="outline" onClick={approve}><CheckCircle2 className="w-4 h-4 mr-1" /> Mark Approved</Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {!event.flyer_approved && (
          <div className="rounded-md border border-yellow-300 bg-yellow-50 text-yellow-900 px-3 py-2 text-xs">
            ⚠️ Awaiting Regional Coordinator Approval before distribution.
          </div>
        )}
        <FlyerPreview ref={flyerRef} form={form} regUrl={regUrl} template={template} />
        <div className="flex flex-wrap gap-2">
          <Button onClick={downloadPng} style={{ backgroundColor: GOLD, color: "white" }}><FileImage className="w-4 h-4 mr-1" /> Download PNG</Button>
          <Button onClick={downloadPdf} variant="outline"><FileText className="w-4 h-4 mr-1" /> Download PDF</Button>
          <Button variant="outline" onClick={() => { navigator.clipboard.writeText(regUrl); toast({ title: "Link copied" }); }}>
            <Download className="w-4 h-4 mr-1" /> Copy Reg Link
          </Button>
          <Button variant="outline" onClick={whatsappShare}><Share2 className="w-4 h-4 mr-1" /> WhatsApp</Button>
        </div>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>
    {children}
  </div>
);

interface FlyerPreviewProps { form: KitEvent; regUrl: string; template: "classic" | "gold" | "minimal"; }
const FlyerPreview = React.forwardRef<HTMLDivElement, FlyerPreviewProps>(({ form, regUrl, template }, ref) => {
  const headerBg = template === "gold" ? GOLD : TEAL;
  const accentBg = template === "gold" ? TEAL : GOLD;
  return (
    <div ref={ref} className="bg-white rounded-lg border shadow-sm overflow-hidden mx-auto" style={{ width: 540, fontFamily: "system-ui, sans-serif" }}>
      <div style={{ backgroundColor: headerBg }} className="text-white text-center py-3 px-4">
        <div className="text-xs tracking-[0.3em] opacity-90">✦  OFUESI {form.region?.toUpperCase()} CONFERENCE  ✦</div>
      </div>
      <div className="p-6 space-y-3 text-gray-800">
        <h3 className="text-2xl font-bold text-center" style={{ color: accentBg }}>{form.title || "Conference Title"}</h3>
        {form.theme && <p className="text-center italic text-sm">"{form.theme}"</p>}
        {form.scripture && <p className="text-center text-xs text-gray-600">{form.scripture}</p>}
        <hr />
        <div className="text-sm space-y-1">
          <div>📅 <strong>Date:</strong> {form.start_date || "TBA"}{form.end_date && form.end_date !== form.start_date ? ` – ${form.end_date}` : ""}</div>
          <div>⏰ <strong>Time:</strong> {form.start_time || "TBA"}{form.end_time ? ` – ${form.end_time}` : ""}</div>
          <div>📍 <strong>Venue:</strong> {[form.venue_name, form.venue_address, form.city, form.state].filter(Boolean).join(", ") || "TBA"}</div>
          {form.speaker_name && <div>👤 <strong>Speaker:</strong> {form.speaker_name}{form.speaker_title ? `, ${form.speaker_title}` : ""}</div>}
          {form.worship_team && <div>🎤 <strong>Worship:</strong> {form.worship_team}</div>}
        </div>
        <div className="rounded-md p-3 text-sm" style={{ backgroundColor: `${accentBg}22` }}>
          <div>🌐 <strong>Register:</strong> {regUrl}</div>
          <div>💳 <strong>Pay Zelle:</strong> OFUESI Organization Account — NOT to individuals</div>
          {(form.coordinator_name || form.coordinator_email || form.coordinator_phone) && (
            <div>📧 {[form.coordinator_name, form.coordinator_email, form.coordinator_phone].filter(Boolean).join(" | ")}</div>
          )}
        </div>
      </div>
      <div style={{ backgroundColor: headerBg }} className="text-white text-center py-2 text-[11px]">
        Hosted by OFUESI {form.region} · Sanctioned by National Council
      </div>
    </div>
  );
});
FlyerPreview.displayName = "FlyerPreview";

// ---------------- New Conference Wizard ----------------
const NewConferenceWizard: React.FC<{
  open: boolean; onOpenChange: (v: boolean) => void; defaultRegion: string; onCreated: (id: string) => void;
}> = ({ open, onOpenChange, defaultRegion, onCreated }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<KitEvent>({ id: "", region: defaultRegion, title: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setStep(1); setForm({ id: "", region: defaultRegion, title: "" }); } }, [open, defaultRegion]);

  const u = (k: keyof KitEvent, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const launch = async () => {
    setSaving(true);
    const { data, error } = await (supabase as any)
      .from("conference_kit_events")
      .insert({
        region: form.region, title: form.title, theme: form.theme, scripture: form.scripture,
        venue_name: form.venue_name, venue_address: form.venue_address, city: form.city, state: form.state,
        start_date: form.start_date || null, end_date: form.end_date || null,
        start_time: form.start_time, end_time: form.end_time,
        speaker_name: form.speaker_name, speaker_title: form.speaker_title, worship_team: form.worship_team,
        status: "planning",
      })
      .select("id")
      .single();
    setSaving(false);
    if (error) { toast({ title: "Failed", description: error.message, variant: "destructive" }); return; }
    toast({ title: "Conference launched" });
    onCreated(data.id);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>New Conference · Step {step} of 4</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {step === 1 && (
            <>
              <Field label="Region"><Input value={form.region} onChange={(e) => u("region", e.target.value)} /></Field>
              <Field label="Conference Title *"><Input value={form.title} onChange={(e) => u("title", e.target.value)} /></Field>
              <Field label="Theme"><Input value={form.theme || ""} onChange={(e) => u("theme", e.target.value)} /></Field>
              <Field label="Scripture"><Input value={form.scripture || ""} onChange={(e) => u("scripture", e.target.value)} /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Start Date"><Input type="date" value={form.start_date || ""} onChange={(e) => u("start_date", e.target.value)} /></Field>
                <Field label="End Date"><Input type="date" value={form.end_date || ""} onChange={(e) => u("end_date", e.target.value)} /></Field>
                <Field label="Start Time"><Input placeholder="9:00 AM" value={form.start_time || ""} onChange={(e) => u("start_time", e.target.value)} /></Field>
                <Field label="End Time"><Input placeholder="5:00 PM" value={form.end_time || ""} onChange={(e) => u("end_time", e.target.value)} /></Field>
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <Field label="Venue Name"><Input value={form.venue_name || ""} onChange={(e) => u("venue_name", e.target.value)} /></Field>
              <Field label="Venue Address"><Input value={form.venue_address || ""} onChange={(e) => u("venue_address", e.target.value)} /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="City"><Input value={form.city || ""} onChange={(e) => u("city", e.target.value)} /></Field>
                <Field label="State"><Input value={form.state || ""} onChange={(e) => u("state", e.target.value)} /></Field>
              </div>
              <Field label="Speaker Name"><Input value={form.speaker_name || ""} onChange={(e) => u("speaker_name", e.target.value)} /></Field>
              <Field label="Speaker Title"><Input value={form.speaker_title || ""} onChange={(e) => u("speaker_title", e.target.value)} /></Field>
              <Field label="Worship Team"><Input value={form.worship_team || ""} onChange={(e) => u("worship_team", e.target.value)} /></Field>
            </>
          )}
          {step === 3 && (
            <Card><CardContent className="py-6 text-sm text-muted-foreground">
              Default fee categories (Adult / Youth / Student / Child) will be added automatically. You can edit them in the Fee & Payments tab after launch.
            </CardContent></Card>
          )}
          {step === 4 && (
            <Card><CardContent className="py-6 space-y-1 text-sm">
              <div><strong>Title:</strong> {form.title || "—"}</div>
              <div><strong>Region:</strong> {form.region || "—"}</div>
              <div><strong>Date:</strong> {form.start_date || "—"} → {form.end_date || "—"}</div>
              <div><strong>Venue:</strong> {[form.venue_name, form.city, form.state].filter(Boolean).join(", ") || "—"}</div>
              <div><strong>Speaker:</strong> {form.speaker_name || "—"}</div>
              <ZellePolicy />
            </CardContent></Card>
          )}
        </div>
        <DialogFooter>
          {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)}>Back</Button>}
          {step < 4 && <Button onClick={() => setStep(step + 1)} disabled={step === 1 && !form.title}>Next</Button>}
          {step === 4 && (
            <Button onClick={launch} disabled={saving} style={{ backgroundColor: TEAL, color: "white" }}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Launch Conference"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConferenceKitDashboard;
